import { useAppContext } from '@/context';
import { logout, updateUserInStorage } from '@/utils/auth';
import { getJson } from '@/utils/fetchOptions';
import { setBugsnagUser } from '@/utils/bugsnag';

// eslint-disable-next-line import/no-cycle
import { useAuthToken, useMutation, useAuth } from '.';

interface UseUserGetProps {
  onSuccess?: (user: Api.UserObject) => void;
  onError?: (error: unknown) => void;
}

export function useUserGet({ onSuccess, onError }: UseUserGetProps = {}) {
  const { setAlert } = useAppContext();
  const { authData, setAuthData } = useAuth();
  const token = useAuthToken();

  const {
    mutate: fetchUser,
    isLoading,
    error
  } = useMutation({
    mutationKey: 'user-get',
    mutationFn: async () =>
      getJson({
        url: `${process.env.NEXT_PUBLIC_BEATLY_API_URL}/app/v2/user`,
        token
      }).then((response: Api.UserObject | ResponseObject) => {
        if ((response as ResponseExpiredToken).isExpired) {
          logout();
        }

        if ('code' in response) {
          setAlert(response.code);
          return {};
        }

        // Update localStorage (persistence) and React state (immediate UI updates)
        updateUserInStorage(response);
        setAuthData({ ...authData, user: response });

        // Update Bugsnag user identification
        setBugsnagUser(response);

        return response;
      }),
    onSuccess: (response: Api.UserObject) => {
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError
  });

  return {
    fetchUser,
    isLoading,
    error
  };
}
